<template>
  <Motion
    class="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 animate-fade-in"
    :initial="{ opacity: 0, y: 16, scale: 0.98 }"
    :animate="{ opacity: 1, y: 0, scale: 1 }"
    :exit="{ opacity: 0, y: 8, scale: 0.98 }"
    :transition="{ type: 'spring', stiffness: 420, damping: 32, mass: 0.9 }"
  >
    <!-- Header -->
    <div class="bg-slate-800/50 border-b border-slate-700/50">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-2xl font-bold text-white">Security Management</h1>
            <p class="text-slate-400 mt-1">Manage RLS policies and security settings</p>
          </div>
          <div class="flex gap-3">
            <button
              @click="runSecurityTests"
              :disabled="testing"
              class="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                ></path>
              </svg>
              {{ testing ? 'Testing...' : 'Run Security Tests' }}
            </button>
            <button
              @click="showCreatePolicyModal = true"
              class="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                ></path>
              </svg>
              Create Policy
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Tabs -->
      <div class="mb-6">
        <div class="border-b border-slate-700">
          <nav class="-mb-px flex space-x-8">
            <button
              @click="activeTab = 'policies'"
              :class="[
                'py-2 px-1 border-b-2 font-medium text-sm transition-colors',
                activeTab === 'policies'
                  ? 'border-primary-500 text-primary-400'
                  : 'border-transparent text-slate-400 hover:text-slate-300 hover:border-slate-300',
              ]"
            >
              RLS Policies
            </button>
            <button
              @click="activeTab = 'testing'"
              :class="[
                'py-2 px-1 border-b-2 font-medium text-sm transition-colors',
                activeTab === 'testing'
                  ? 'border-primary-500 text-primary-400'
                  : 'border-transparent text-slate-400 hover:text-slate-300 hover:border-slate-300',
              ]"
            >
              Security Testing
            </button>
            <button
              @click="activeTab = 'audit'"
              :class="[
                'py-2 px-1 border-b-2 font-medium text-sm transition-colors',
                activeTab === 'audit'
                  ? 'border-primary-500 text-primary-400'
                  : 'border-transparent text-slate-400 hover:text-slate-300 hover:border-slate-300',
              ]"
            >
              Audit Logs
            </button>
          </nav>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="flex items-center justify-center py-12">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
        <span class="ml-3 text-slate-400">Loading security data...</span>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="bg-red-900/20 border border-red-500/30 rounded-lg p-4 mb-6">
        <div class="flex items-center">
          <svg
            class="w-5 h-5 text-red-400 mr-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            ></path>
          </svg>
          <div>
            <h3 class="text-red-400 font-medium">Error</h3>
            <p class="text-red-300 text-sm mt-1">{{ error }}</p>
          </div>
        </div>
      </div>

      <!-- Policies Tab -->
      <div v-else-if="activeTab === 'policies'">
        <!-- RLS Policies Table -->
        <div class="bg-slate-800/30 rounded-lg border border-slate-700/50 overflow-hidden mb-6">
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead class="bg-slate-700/50">
                <tr>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider"
                  >
                    Table
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider"
                  >
                    Policy
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider"
                  >
                    Type
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider"
                  >
                    Status
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-700/50">
                <tr v-for="policy in rlsPolicies" :key="policy.id" class="hover:bg-slate-700/30">
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">
                    {{ policy.table_name }}
                  </td>
                  <td class="px-6 py-4">
                    <div class="text-sm text-white">{{ policy.policy_name }}</div>
                    <div class="text-xs text-slate-400">{{ policy.description }}</div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span
                      :class="[
                        'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium',
                        policy.policy_type === 'SELECT'
                          ? 'bg-blue-900/20 text-blue-300'
                          : policy.policy_type === 'INSERT'
                            ? 'bg-green-900/20 text-green-300'
                            : policy.policy_type === 'UPDATE'
                              ? 'bg-yellow-900/20 text-yellow-300'
                              : policy.policy_type === 'DELETE'
                                ? 'bg-red-900/20 text-red-300'
                                : 'bg-slate-900/20 text-slate-300',
                      ]"
                    >
                      {{ policy.policy_type }}
                    </span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span
                      :class="[
                        'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium',
                        policy.enabled
                          ? 'bg-green-900/20 text-green-300'
                          : 'bg-red-900/20 text-red-300',
                      ]"
                    >
                      {{ policy.enabled ? 'Enabled' : 'Disabled' }}
                    </span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div class="flex items-center gap-2">
                      <button
                        @click="editPolicy(policy)"
                        class="text-blue-400 hover:text-blue-300 transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        @click="togglePolicy(policy)"
                        :class="[
                          'transition-colors',
                          policy.enabled
                            ? 'text-red-400 hover:text-red-300'
                            : 'text-green-400 hover:text-green-300',
                        ]"
                      >
                        {{ policy.enabled ? 'Disable' : 'Enable' }}
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Testing Tab -->
      <div v-else-if="activeTab === 'testing'">
        <!-- Security Test Results -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <!-- Test Summary -->
          <div class="bg-slate-800/30 rounded-lg border border-slate-700/50 p-6">
            <h3 class="text-lg font-medium text-white mb-4">Test Summary</h3>
            <div class="space-y-3">
              <div class="flex items-center justify-between">
                <span class="text-slate-300">Total Tests</span>
                <span class="text-white font-medium">{{ testResults.total }}</span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-slate-300">Passed</span>
                <span class="text-green-400 font-medium">{{ testResults.passed }}</span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-slate-300">Failed</span>
                <span class="text-red-400 font-medium">{{ testResults.failed }}</span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-slate-300">Success Rate</span>
                <span class="text-white font-medium">{{ testResults.successRate }}%</span>
              </div>
            </div>
          </div>

          <!-- Quick Actions -->
          <div class="bg-slate-800/30 rounded-lg border border-slate-700/50 p-6">
            <h3 class="text-lg font-medium text-white mb-4">Quick Actions</h3>
            <div class="space-y-3">
              <button
                @click="runSecurityTests"
                :disabled="testing"
                class="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  ></path>
                </svg>
                Run All Tests
              </button>
              <button
                @click="scheduleTests"
                class="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  ></path>
                </svg>
                Schedule Tests
              </button>
            </div>
          </div>
        </div>

        <!-- Test Results -->
        <div class="bg-slate-800/30 rounded-lg border border-slate-700/50 overflow-hidden">
          <div class="px-6 py-4 border-b border-slate-700/50">
            <h3 class="text-lg font-medium text-white">Test Results</h3>
          </div>
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead class="bg-slate-700/50">
                <tr>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider"
                  >
                    Test
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider"
                  >
                    Status
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider"
                  >
                    Duration
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider"
                  >
                    Details
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-700/50">
                <tr v-for="test in securityTests" :key="test.id" class="hover:bg-slate-700/30">
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="text-sm font-medium text-white">{{ test.name }}</div>
                    <div class="text-xs text-slate-400">{{ test.description }}</div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span
                      :class="[
                        'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium',
                        test.status === 'PASS'
                          ? 'bg-green-900/20 text-green-300'
                          : test.status === 'FAIL'
                            ? 'bg-red-900/20 text-red-300'
                            : 'bg-yellow-900/20 text-yellow-300',
                      ]"
                    >
                      {{ test.status }}
                    </span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-400">
                    {{ test.duration }}ms
                  </td>
                  <td class="px-6 py-4">
                    <button
                      @click="viewTestDetails(test)"
                      class="text-blue-400 hover:text-blue-300 transition-colors text-sm"
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Audit Tab -->
      <div v-else-if="activeTab === 'audit'">
        <!-- Audit Logs -->
        <div class="bg-slate-800/30 rounded-lg border border-slate-700/50 overflow-hidden">
          <div class="px-6 py-4 border-b border-slate-700/50">
            <h3 class="text-lg font-medium text-white">Audit Logs</h3>
          </div>
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead class="bg-slate-700/50">
                <tr>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider"
                  >
                    Timestamp
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider"
                  >
                    User
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider"
                  >
                    Action
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider"
                  >
                    Resource
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider"
                  >
                    Result
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-700/50">
                <tr v-for="log in auditLogs" :key="log.id" class="hover:bg-slate-700/30">
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-400">
                    {{ formatDate(log.timestamp) }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-white">
                    {{ log.user_email }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-white">
                    {{ log.action }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-400">
                    {{ log.resource }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span
                      :class="[
                        'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium',
                        log.result === 'SUCCESS'
                          ? 'bg-green-900/20 text-green-300'
                          : 'bg-red-900/20 text-red-300',
                      ]"
                    >
                      {{ log.result }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Create Policy Modal -->
    <div
      v-if="showCreatePolicyModal"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
    >
      <div class="bg-slate-800 rounded-lg p-6 w-full max-w-2xl mx-4">
        <h3 class="text-lg font-medium text-white mb-4">Create RLS Policy</h3>

        <form @submit.prevent="createPolicy">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Table Name</label>
              <input
                v-model="newPolicy.table_name"
                type="text"
                required
                class="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="e.g., users"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Policy Name</label>
              <input
                v-model="newPolicy.policy_name"
                type="text"
                required
                class="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="e.g., users_select_policy"
              />
            </div>
          </div>

          <div class="mb-4">
            <label class="block text-sm font-medium text-slate-300 mb-2">Policy Type</label>
            <select
              v-model="newPolicy.policy_type"
              class="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="SELECT">SELECT</option>
              <option value="INSERT">INSERT</option>
              <option value="UPDATE">UPDATE</option>
              <option value="DELETE">DELETE</option>
            </select>
          </div>

          <div class="mb-6">
            <label class="block text-sm font-medium text-slate-300 mb-2">Policy Definition</label>
            <textarea
              v-model="newPolicy.definition"
              class="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="USING (user_has_permission(auth.uid(), 'view_users'))"
              rows="4"
            ></textarea>
          </div>

          <div class="flex justify-end gap-3">
            <button
              type="button"
              @click="showCreatePolicyModal = false"
              class="px-4 py-2 text-slate-300 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              :disabled="createLoading"
              class="px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white rounded-lg transition-colors"
            >
              {{ createLoading ? 'Creating...' : 'Create Policy' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </Motion>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { Motion } from 'motion-v'
import { createLogger } from '@/lib/logger'
import { supabase } from '@/lib/supabaseClient'

const log = createLogger('SecurityManagement')

// State
const activeTab = ref<'policies' | 'testing' | 'audit'>('policies')
const loading = ref(false)
const error = ref<string | null>(null)
const testing = ref(false)
const showCreatePolicyModal = ref(false)
const createLoading = ref(false)

// Data
const rlsPolicies = ref<any[]>([])
const securityTests = ref<any[]>([])
const auditLogs = ref<any[]>([])

// New policy form
const newPolicy = ref({
  table_name: '',
  policy_name: '',
  policy_type: 'SELECT',
  definition: '',
})

// Computed
const testResults = computed(() => {
  const total = securityTests.value.length
  const passed = securityTests.value.filter((t) => t.status === 'PASS').length
  const failed = securityTests.value.filter((t) => t.status === 'FAIL').length
  const successRate = total > 0 ? Math.round((passed / total) * 100) : 0

  return { total, passed, failed, successRate }
})

// Load data on mount
onMounted(() => {
  loadRLSPolicies()
  loadSecurityTests()
  loadAuditLogs()
})

// Load RLS policies
async function loadRLSPolicies() {
  loading.value = true
  error.value = null

  try {
    // Mock data for now - in real implementation, this would query Supabase
    rlsPolicies.value = [
      {
        id: '1',
        table_name: 'users',
        policy_name: 'users_select_policy',
        policy_type: 'SELECT',
        enabled: true,
        description: 'Allow users to view their own data',
      },
      {
        id: '2',
        table_name: 'roles',
        policy_name: 'roles_manage_policy',
        policy_type: 'ALL',
        enabled: true,
        description: 'Allow admins to manage roles',
      },
    ]
  } catch (err) {
    log.error('Failed to load RLS policies:', err)
    error.value = err instanceof Error ? err.message : 'Failed to load RLS policies'
  } finally {
    loading.value = false
  }
}

// Load security tests
async function loadSecurityTests() {
  try {
    // Mock data for now
    securityTests.value = [
      {
        id: '1',
        name: 'RLS Policy Enforcement',
        description: 'Test that RLS policies are properly enforced',
        status: 'PASS',
        duration: 150,
      },
      {
        id: '2',
        name: 'Permission Validation',
        description: 'Test user permission validation',
        status: 'PASS',
        duration: 89,
      },
      {
        id: '3',
        name: 'Role Assignment',
        description: 'Test role assignment and removal',
        status: 'FAIL',
        duration: 234,
      },
    ]
  } catch (err) {
    log.error('Failed to load security tests:', err)
  }
}

/**
 * Load audit logs from pricing_audit_logs (the only audit table that exists).
 * A general audit_logs table is planned but not yet created.
 */
async function loadAuditLogs() {
  try {
    const { data, error: queryErr } = await supabase
      .from('pricing_audit_logs')
      .select('id, action, table_name, changed_by, created_at, old_values, new_values')
      .order('created_at', { ascending: false })
      .limit(50)

    if (queryErr) {
      log.warn('pricing_audit_logs query failed (table may not exist):', queryErr.message)
      auditLogs.value = []
      return
    }

    auditLogs.value = (data || []).map((row: any) => ({
      id: row.id,
      timestamp: row.created_at,
      user_email: row.changed_by || '—',
      action: String(row.action || '').toUpperCase(),
      resource: row.table_name || 'pricing',
      result: 'SUCCESS',
    }))
  } catch (err) {
    log.error('Failed to load audit logs:', err)
    auditLogs.value = []
  }
}

// Run security tests
async function runSecurityTests() {
  testing.value = true

  try {
    // Simulate test execution
    await new Promise((resolve) => setTimeout(resolve, 2000))

    // Update test results
    securityTests.value = securityTests.value.map((test) => ({
      ...test,
      status: Math.random() > 0.2 ? 'PASS' : 'FAIL',
      duration: Math.floor(Math.random() * 200) + 50,
    }))
  } catch (err) {
    log.error('Failed to run security tests:', err)
    error.value = err instanceof Error ? err.message : 'Failed to run security tests'
  } finally {
    testing.value = false
  }
}

// Schedule tests
function scheduleTests() {
  // TODO: Implement test scheduling
  log.debug('Schedule tests')
}

// Create policy
async function createPolicy() {
  createLoading.value = true

  try {
    // TODO: Implement policy creation
    log.debug('Create policy:', newPolicy.value)
    showCreatePolicyModal.value = false
    newPolicy.value = {
      table_name: '',
      policy_name: '',
      policy_type: 'SELECT',
      definition: '',
    }
  } catch (err) {
    log.error('Failed to create policy:', err)
    error.value = err instanceof Error ? err.message : 'Failed to create policy'
  } finally {
    createLoading.value = false
  }
}

// Edit policy
function editPolicy(policy: any) {
  // TODO: Implement policy editing
  log.debug('Edit policy:', policy)
}

// Toggle policy
function togglePolicy(policy: any) {
  policy.enabled = !policy.enabled
  // TODO: Implement policy toggle
  log.debug('Toggle policy:', policy)
}

// View test details
function viewTestDetails(test: any) {
  // TODO: Implement test details view
  log.debug('View test details:', test)
}

// Format date
function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleString()
}
</script>
